<script setup lang="ts">
const { locale, t, locales } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()
const switchLocalePath = useSwitchLocalePath()

// Drive the pointer-tracked spotlight on every .rod-card across the site.
// Single document-level listener — set up once at root, lives for the
// entire SPA session.
useSpotlight()

const siteUrl = config.public.siteUrl as string

// Map our 6 locales to the OG locale codes Facebook/Twitter want.
const ogLocaleMap: Record<string, string> = {
  ru: 'ru_RU',
  en: 'en_US',
  fr: 'fr_FR',
  es: 'es_ES',
  de: 'de_DE',
  it: 'it_IT',
}

// This page in another locale: /en/partners/ → /fr/partners/, /partners/ …
// It used to be built from the locale alone, so every page named the home
// pages as its other-language versions, against sitemap.xml.
const withSlash = (path: string) => (path.endsWith('/') ? path : `${path}/`)
const localizedUrl = (code: string) =>
  `${siteUrl}${withSlash(switchLocalePath(code) || (code === 'ru' ? '/' : `/${code}`))}`

const canonical = computed(() => {
  const path = route.path === '/' ? '/' : route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

// Per-locale title + description, canonical, hreflang alternates, and OG
// locale stack. Reactive on i18n locale change.
useHead(() => ({
  htmlAttrs: { lang: locale.value },
  title:     t('seo.title'),
  meta: [
    { name: 'description',         content: t('seo.description') },
    { name: 'keywords',            content: t('seo.keywords') },
    { property: 'og:locale',       content: ogLocaleMap[locale.value] ?? 'en_US' },
    ...(locales.value as Array<{ code: string }>)
      .filter((l) => l.code !== locale.value)
      .map((l) => ({ property: 'og:locale:alternate', content: ogLocaleMap[l.code] ?? 'en_US' })),
    { property: 'og:title',        content: t('seo.title') },
    { property: 'og:description',  content: t('seo.description') },
    { property: 'og:url',          content: canonical.value },
    { name: 'twitter:title',       content: t('seo.title') },
    { name: 'twitter:description', content: t('seo.description') },
  ],
  link: [
    { rel: 'canonical', href: canonical.value },
    // hreflang alternates — one per locale + x-default (the Russian page),
    // with the bare language codes sitemap.xml uses: a region subtag
    // (en-US) asks search engines to target that country.
    ...(locales.value as Array<{ code: string }>).map((l) => ({
      rel:      'alternate',
      hreflang: l.code,
      href:     localizedUrl(l.code),
    })),
    { rel: 'alternate', hreflang: 'x-default', href: localizedUrl('ru') },
  ],
  script: [
    {
      type: 'application/ld+json',
      // Organization schema — gives Google the brand card.
      innerHTML: JSON.stringify({
        '@context':    'https://schema.org',
        '@type':       'Organization',
        name:          'RomBa',
        url:           `${siteUrl}/`,
        logo:          `${siteUrl}/favicon.svg`,
        description:   t('seo.description'),
        sameAs:        [],
      }),
    },
    {
      type: 'application/ld+json',
      // WebSite schema — enables sitelinks search box if/when we add a search endpoint.
      innerHTML: JSON.stringify({
        '@context':    'https://schema.org',
        '@type':       'WebSite',
        name:          'RomBa',
        url:           `${siteUrl}/`,
        inLanguage:    ['ru', 'hy', 'en', 'fr', 'es', 'de', 'it'],
      }),
    },
    {
      type: 'application/ld+json',
      // Service schema — the actual product description for rich snippets.
      innerHTML: JSON.stringify({
        '@context':    'https://schema.org',
        '@type':       'Service',
        serviceType:   'Peer-to-peer duty-free courier delivery',
        provider:      { '@type': 'Organization', name: 'RomBa' },
        // Goods are handed over in Armenia only (Zvartnots); the departure
        // duty-frees (DXB, MXP, TBS) are where they're bought, not served.
        areaServed:    ['AM'],
        description:   t('seo.description'),
        url:           canonical.value,
      }),
    },
  ],
}))
</script>

<template>
  <UApp>
    <NuxtPage />
  </UApp>
</template>
